import type { WriteTier } from "./types";

/**
 * The write-tier registry: CLAUDE.md section 3, Master Spec B5.
 *
 * Every named category of change an agent could want to make, and who may
 * make it. T1 ships after floors, eval regression and a challenger shadow. T2
 * needs a human sign-off. T3 is human-only, permanently: "Auto-learn and every
 * other agent are physically unable to write these."
 *
 * Until this file `WriteTier` existed in `types.ts` and nothing referenced it,
 * so the T3 wall was prose. It is still prose at runtime, because no learner
 * exists yet to propose anything; what this file adds is that the wall is now
 * a type (`AgentProposableCategory` cannot name a T3 category) and a runtime
 * refusal (`assertAgentMayPropose`) that fails closed, ready before the first
 * proposal path is written rather than retrofitted after it.
 *
 * This table is itself Learning Charter content: B16 lists "tier permissions"
 * among what the charter holds, and the charter is T3. Moving a category
 * between tiers is a human edit of this file, reviewed like any other T3
 * change, and `writeTiers.test.ts` restates every row so the edit cannot be a
 * one-line diff.
 *
 * Names are CLAUDE.md section 3's, kebab-cased. Where the vault is narrower or
 * broader the doc comment on the row says so. Categories the vault tiers that
 * section 3 does not list (the scenario map as a whole, skills,
 * signal weights, memory merges, label schema, frozen baselines) are
 * deliberately absent, and `assertAgentMayPropose` refuses anything absent.
 * `lessons` is the one exception, registered T2 by the conservative reading
 * of TA-861 (R8.2) until Kabir rules; see its row.
 */
export const WRITE_TIERS = Object.freeze({
  // T3: humans only, forever.

  /** Guardrails (B5 T3). */
  guardrails: "T3",
  /** Escalation triggers (B5 T3). */
  "escalation-triggers": "T3",
  /** SLA timers, "hard-coded (T3)" (Agent Brains 5.5, the shepherd's L4). */
  "sla-timers": "T3",
  /** Price caps (B5 "price/discount caps"). */
  "price-caps": "T3",
  /** Discount caps (B5 "price/discount caps"), including cumulative concession caps. */
  "discount-caps": "T3",
  /** FTC Jewelry Guides, lab-grown disclosure, review solicitation (B5 T3). */
  "compliance-rules": "T3",
  /**
   * Any price or quote differentiated by inferred wealth (B2 VIP class,
   * "Prohibited permanently (T3)"). VIP changes service level and routing,
   * never a number.
   */
  "wealth-differentiated-pricing": "T3",
  /** The messaging-window state machine (Inbox Composer section 6, "a hard T3 gate"). */
  "messaging-window-state-machine": "T3",
  /** The Learning Charter itself (B16 source 1, "T3, humans-only"). */
  "learning-charter": "T3",
  /** Auto-learn's own validation bars and floors (B16: charter content). */
  "auto-learn-validation-bars": "T3",
  /** Adaptive reward weights (B7.4, "REJECTED anti-pattern (T3)"). */
  "adaptive-reward-weights": "T3",
  /** Class D scenario rows: complaints, fraud-suspect, unknown (Agent Brains section 9). */
  "class-d-scenario-rows": "T3",

  // T2: a human signs off.

  /** Voice profile (B5 T2). */
  "voice-profile": "T2",
  /** KB facts (B5 T2). */
  "kb-facts": "T2",
  /**
   * The per-client config artifact (Y1 TENANCY). Decision 367 (OPEN row 314): T2, a human signs off, by
   * analogy with `voice-profile` in CLAUDE.md section 3's T2 list. A draft may be written; going live still
   * needs a person's approval. It holds no cap, guardrail or window rule: those stay the T3 rows above.
   */
  "client-config": "T2",
  /**
   * The lessons artifact (TA-861, R8.2). Section 3 does not list it; the conservative reading is T2, by the
   * same analogy decision 367 used for `client-config`: a lesson is a learned instruction that would shape a
   * prompt, as the voice profile does. A draft may be written; a human signs it before it is live; and the
   * reply prompt reads it only under ACKRA_LESSONS_LOAD (default off, OPEN-FOR-KABIR row 255). Kabir's ruling
   * on TA-861 may move or drop this row. Never T1: a lesson is not a pattern that ships on floors alone.
   */
  lessons: "T2",
  /**
   * Qualification and scoring thresholds (B5 "qualification thresholds",
   * Agent Brains "threshold moves"). Never a cap, a floor, a validation bar
   * or an SLA timer: those are the T3 rows above, and a threshold that is
   * also one of them is T3.
   */
  thresholds: "T2",
  /**
   * Promoting a scenario to auto-send (B6: "the HUMAN flips the switch",
   * Agent Brains section 9: class promotions are T2). Auto-demote stays
   * automatic and is not a proposal.
   */
  "graduation-flags": "T2",
  /** Memory prunes: "deleting a customer memory ... T2" (B2 memory lifecycle). */
  "memory-prunes": "T2",
  /** A model version bump must pass the golden evals (Agent Brains section 11). */
  "model-version-bumps": "T2",
  /** Eval-set refresh (B15.5 "eval-refresh proposal, T2", B16 "refresh = human sign-off"). */
  "eval-refreshes": "T2",

  // T1: auto-shippable after floors, eval regression and a challenger shadow.

  /** Playbook patterns (B5 T1). Single-client ship rules are F-15's, not this table's. */
  patterns: "T1",
  /** Few-shot swaps (B5 T1). */
  "few-shots": "T1",
  /** Timing and cadence (B5 T1). */
  cadence: "T1",
  /** Retrieval weights (B5 T1). */
  "retrieval-weights": "T1",
} as const satisfies Readonly<Record<string, WriteTier>>);

/** Every registered write category. */
export type WriteCategory = keyof typeof WRITE_TIERS;

/** The categories registered at one tier (or a union of tiers). */
export type CategoryOfTier<T extends WriteTier> = {
  [K in WriteCategory]: (typeof WRITE_TIERS)[K] extends T ? K : never;
}[WriteCategory];

/** T3: no agent may name one of these in a proposal. */
export type HumanOnlyCategory = CategoryOfTier<"T3">;

/**
 * What an agent may propose: T1 and T2 only. A proposal type keyed on this
 * cannot carry a T3 category, so the refusal happens at compile time for
 * typed callers, and `assertAgentMayPropose` repeats it at runtime for
 * proposals that arrive as data.
 */
export type AgentProposableCategory = CategoryOfTier<"T1" | "T2">;

/**
 * TA-318. What `assertAgentMayPropose` throws, as a class a caller can catch.
 *
 * The messages are unchanged; the type is the addition. A proposal path has to
 * tell a policy refusal ("no agent may write this, ever") apart from a dropped
 * connection to know whether retrying is honest, and matching on message text
 * to do it is the shape that rots. `packages/db/src/artifactStore.ts` already
 * makes this argument for the T2 gate with `ArtifactApprovalError`; this is the
 * same argument one tier up.
 *
 * `tier` is `"T3"` when the registry holds the category and puts it beyond
 * every agent, and null when the registry does not hold it at all. Never `"T1"`
 * or `"T2"`: those are not refused, so they never reach this class.
 */
export class WriteTierRefusal extends Error {
  /** The category the proposal named, verbatim, registered or not. */
  readonly category: string;
  readonly tier: Extract<WriteTier, "T3"> | null;

  constructor(input: {
    readonly category: string;
    readonly tier: Extract<WriteTier, "T3"> | null;
    readonly message: string;
  }) {
    super(input.message);
    this.name = "WriteTierRefusal";
    this.category = input.category;
    this.tier = input.tier;
  }
}

/**
 * Throws unless `category` is a registered T1 or T2 category.
 *
 * Takes a plain string because proposals arrive as data (a learner's JSON, a
 * queue row), and narrows it on success. Fails closed: an unregistered name
 * is refused rather than treated as proposable, since a typo or a new,
 * untiered kind of change is exactly the case a human has not decided yet
 * (section 0, when unsure, don't). `Object.hasOwn`, not `in` or a bracket
 * read, so `toString` and `__proto__` are not categories.
 *
 * TA-318: a caller that must also RECORD the refusal goes through
 * `guardAgentProposal` (`packages/db/src/proposalGuard.ts`) rather than calling
 * this directly, so the record and the refusal cannot come apart.
 */
export function assertAgentMayPropose(category: string): asserts category is AgentProposableCategory {
  // TA-391. `Object.hasOwn` turns its key into a string, so `["patterns"]`, or
  // an object whose `toString` says "patterns", resolved as that category. The
  // type says string, but a proposal arrives as data, so the check is repeated
  // here. The refusal names the value's kind, never `String(value)`, which
  // throws on a null-prototype object.
  if (typeof category !== "string") {
    const kind: string = category === null ? "null" : Array.isArray(category) ? "array" : typeof category;
    throw new WriteTierRefusal({
      category: `(${kind})`,
      tier: null,
      message:
        `write category is not a string (got ${kind}), so no agent may propose it ` +
        "(CLAUDE.md section 3, packages/core/src/writeTiers.ts)",
    });
  }
  if (!Object.hasOwn(WRITE_TIERS, category)) {
    throw new WriteTierRefusal({
      category,
      tier: null,
      message:
        `write category "${category}" is not in the write-tier registry, so no agent may propose it ` +
        "until a human tiers it (CLAUDE.md section 3, packages/core/src/writeTiers.ts)",
    });
  }
  const tier: WriteTier = WRITE_TIERS[category as WriteCategory];
  if (tier === "T3") {
    throw new WriteTierRefusal({
      category,
      tier,
      message:
        `write category "${category}" is T3: human-only, permanently, never agent-writable ` +
        "and never agent-proposable (CLAUDE.md section 3)",
    });
  }
}
